import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./tilesGrid.html";
import { getBooleanAttr, getObjectAttr } from 'wds/common';
import { responsiveBindingMobile } from '~/components/wds/common/responsiveHelper';
import { WireI18n } from '~/modules/stores/i18nStore';
import { GridVariants, TilesDataFormat } from './enums';
class TilesGrid extends LightningElement {
  constructor(...args) {
    super(...args);
    this.format = void 0;
    this.title = void 0;
    this._variant = GridVariants.Stack;
    this.showMore = void 0;
    this.name = void 0;
    this._isError = false;
    this._data = [];
    this._isSponsored = false;
    this.sponsoredLabel = 'Sponsored';
    this.labelChatBadge = 'Chat';
    this._labels = {};
    this.gridAxis = void 0;
    this.gridFlow = void 0;
    this.gridRowGap = void 0;
    this.gridGap = void 0;
    this.gridFlex = void 0;
    this.gridAlignItems = void 0;
    this.gridItemSize = void 0;
    this.loadedRecordKeys = [];
    this.customTitle = false;
    this.subTitle = void 0;
    this._isLoading = false;
    this._isMobile = void 0;
  }
  i18nCallback(i18n) {
    this.staticLabels = i18n.error.componentLoadFailed;
  }
  set labels(value) {
    this._labels = value;
  }
  get labels() {
    return this._labels;
  }
  set isLoading(value) {
    this._isLoading = getBooleanAttr(value);
  }
  get isLoading() {
    return this._isLoading;
  }
  set variant(value) {
    this._variant = value;
  }
  get variant() {
    return this._variant;
  }
  set isSponsored(value) {
    this._isSponsored = getBooleanAttr(value);
  }
  get isSponsored() {
    return this._isSponsored;
  }
  set isError(value) {
    this._isError = getBooleanAttr(value);
  }
  get isError() {
    return this._isError;
  }
  generateKeys(records) {
    return records?.map(record => ({
      ...record,
      key: `${this._sectionId}-${record.oafId || record.id}`
    }));
  }
  set records(value) {
    if (typeof value === 'string') {
      try {
        this._data = this.generateKeys(JSON.parse(value));
      } catch (error) {
        throw new Error(`the json value ${value} couldnt set to the data property`);
      }
    } else {
      this._data = value.length > 0 ? this.generateKeys(value) : value;
    }
  }
  get records() {
    return this._data ?? [];
  }
  get hasRecords() {
    return this.records.length > 0;
  }
  get noRecords() {
    return !this.isLoading && !this.hasRecords;
  }
  get errorOrNoRecords() {
    return this.noRecords || this.isError;
  }
  set props(value) {
    const props = getObjectAttr(value);
    if (props) {
      this.title = props.title;
      this.format = props.format;
      this.name = props.type;
      this._sectionId = props.id;
      this.addData(this.generateKeys(props.records));
      if (props.variant) {
        this._variant = props.variant;
      }
      if (props.showMore) {
        this.showMore = props.showMore;
      }
    }
  }
  get props() {
    return {
      title: this.title,
      name: this.name,
      format: this.format,
      callToAction: this.showMore,
      id: this._sectionId
    };
  }
  get empty() {
    return this.variant === GridVariants.Stack ? [0, 1, 2] : [0, 1];
  }
  get isContent() {
    return this.format === TilesDataFormat.Content;
  }
  get isListing() {
    return this.format === TilesDataFormat.Listing;
  }
  get isMobile() {
    return this._isMobile;
  }
  get displayShowMore() {
    return this.showMore && !this._isLoading;
  }
  get showHeader() {
    return this.title || this.showMore;
  }
  get titleClass() {
    return this.showSubTitle ? 'sub-title' : 'title';
  }
  get showMoreClass() {
    return this.showSubTitle ? 'showmore-top sub-title' : 'showmore-top';
  }
  get showMoreSymbol() {
    return this.showSubTitle ? 'chevronright' : 'forward';
  }
  get showSubTitle() {
    return !!this.subTitle && !this.isLoading;
  }
  connectedCallback() {
    responsiveBindingMobile(matches => {
      this._isMobile = matches;
    });
  }
  handleRetry() {
    const retryEvent = new CustomEvent('retry', {
      detail: this,
      composed: true,
      bubbles: true,
      cancelable: true
    });
    this.dispatchEvent(retryEvent);
  }
  handleAnalytics(event) {
    event.detail.tiles = this.props;
  }
  get level() {
    return this._isMobile ? '7' : '2';
  }
  get showGridClass() {
    return this.isLoading ? 'hidden' : '';
  }

  // @ts-expect-error * Automated Type Error Fix
  recordId(record) {
    return record.oafId || record.id;
  }

  // @ts-expect-error * Automated Type Error Fix
  keys(items) {
    // @ts-expect-error * Automated Type Error Fix
    return items.map(item => this.recordId(item));
  }
  addNewData(newItems) {
    newItems.forEach(newItem => {
      if (!this.keys(this._data).includes(this.recordId(newItem))) {
        this._data.push(newItem);
      }
    });
  }
  startsWith(newItems) {
    return this._data.every((oldItem, index) => {
      const newItemInPosition = newItems[index];
      return newItemInPosition && this.recordId(oldItem) === this.recordId(newItemInPosition);
    });
  }
  addData(newItems) {
    if (!newItems) {
      return;
    }
    if (this.startsWith(newItems)) {
      this.addNewData(newItems);
    } else {
      this._data = newItems;
    }
  }
  /*LWC compiler v7.1.3*/
}
TilesGrid.delegatesFocus = true;
_registerDecorators(TilesGrid, {
  publicProps: {
    sponsoredLabel: {
      config: 0
    },
    labelChatBadge: {
      config: 0
    },
    gridAxis: {
      config: 0
    },
    gridFlow: {
      config: 0
    },
    gridRowGap: {
      config: 0
    },
    gridGap: {
      config: 0
    },
    gridFlex: {
      config: 0
    },
    gridAlignItems: {
      config: 0
    },
    gridItemSize: {
      config: 0
    },
    customTitle: {
      config: 0
    },
    subTitle: {
      config: 0
    },
    labels: {
      config: 3
    },
    isLoading: {
      config: 3
    },
    variant: {
      config: 3
    },
    isSponsored: {
      config: 3
    },
    isError: {
      config: 3
    },
    records: {
      config: 3
    },
    props: {
      config: 3
    }
  },
  track: {
    format: 1,
    title: 1,
    showMore: 1,
    name: 1,
    _data: 1,
    loadedRecordKeys: 1,
    _isLoading: 1,
    _isMobile: 1
  },
  wire: {
    i18nCallback: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    }
  },
  fields: ["_variant", "_isError", "_isSponsored", "_labels"]
});
const __lwc_component_class_internal = _registerComponent(TilesGrid, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;